import './PanelTabs.css'

/**
 * FindingsTab - Clickable findings list. Click a finding to view its data on the left.
 *
 * Props:
 *   findings: array of { type, title, detail, source, data?, sql?, rowCount?, provenance? }
 *   selectedIndex: number | null
 *   onSelect: function(index) - called when a finding is clicked
 *   onProvenanceClick: function(provenanceSource) - called when a provenance source is clicked
 *   onPin: function(finding) - called when pin button is clicked (optional)
 */
export default function FindingsTab({ findings = [], customerSummary = null, customerError = null, customerLoading = false, selectedIndex = null, onSelect, onProvenanceClick, onPin }) {
  // The customer's findings could not be read: said, never shown as "no findings".
  const customerLine = customerError
    ? <div role="alert" style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>Couldn&apos;t load the customer&apos;s findings: {customerError}</div>
    : null
  if (findings.length === 0 && customerError) return <div>{customerLine}</div>
  if (findings.length === 0 && customerLoading) {
    return <div style={{ fontSize: 12, color: 'var(--gray-400)', padding: '20px 0' }}>Loading findings…</div>
  }
  if (findings.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--gray-400)' }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>🔍</div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>No findings yet</div>
        <div style={{ fontSize: 12 }}>
          Run an investigation to surface findings, risks, and gaps.
        </div>
      </div>
    )
  }

  const typeLabels = {
    critical: 'Critical',
    warning: 'Warning',
    info: 'Info',
    fyi: 'FYI',
    compatible: 'Compatible',
    blocked: 'Blocked',
  }

  const truthTypeLabels = {
    truth_1_data: 'Data',
    truth_2_requirements: 'Requirements',
    truth_3_law: 'Compliance',
    truth_4_product: 'Product',
    truth_5_config: 'Configuration',
  }

  // Customer findings are counted by the route's summary (the platform's tiers; FYI is
  // listed, never counted). This investigation's own findings are counted here.
  const investigation = findings.filter(f => !f.isObservation)
  const fyiCount = findings.filter(f => f.isObservation && f.type === 'fyi').length
  const notChecked = findings.filter(f => f.isObservation && f.couldNotCheck).length
  const count = (type) => investigation.filter(f => f.type === type).length

  return (
    <div>
      {customerLine}
      {customerSummary && (
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 6 }}>
          Customer findings: {customerSummary.critical || 0} critical, {customerSummary.warning || 0} warning, {customerSummary.info || 0} info
          {fyiCount > 0 && ` (+${fyiCount} FYI, listed not counted)`}
          {notChecked > 0 && ` · ${notChecked} couldn't be checked in the last scan`}
        </div>
      )}
      {investigation.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 6 }}>
          This investigation: {count('critical')} critical, {count('warning')} warnings, {count('compatible')} compatible, {count('blocked')} blocked
        </div>
      )}
      <div style={{ marginBottom: 8 }} />

      {findings.map((f, i) => {
        const hasData = f.data && f.data.length > 0
        const isClickable = hasData || f.isObservation || f.rowCount > 0
        const hasProv = f.provenance && f.provenance.length > 0
        const isSelected = selectedIndex === i
        const isLoading = f._drillLoading
        return (
          <div
            key={i}
            className={`finding-card ${f.type} ${isClickable ? 'finding-clickable' : ''} ${isSelected ? 'finding-selected' : ''}`}
            onClick={() => isClickable && onSelect?.(isSelected ? null : i)}
          >
            <div className="finding-card-top">
              <span className={`finding-tag ${f.type}`}>
                {typeLabels[f.type] || f.type}
              </span>
              {f.couldNotCheck && (
                <span
                  className="finding-data-hint"
                  style={{ color: 'var(--amber)' }}
                  title={[f.lastCheckReason, f.lastCheckedAt && `last scan ${new Date(f.lastCheckedAt).toLocaleString()}`].filter(Boolean).join(' · ')}
                >
                  couldn&apos;t check
                </span>
              )}
              {isLoading && (
                <span className="finding-data-hint" style={{ color: 'var(--gray-400)' }}>
                  Loading…
                </span>
              )}
              {!isLoading && isClickable && (
                <span className="finding-data-hint">
                  {isSelected ? '✕ Close' : `${f.rowCount || (f.data && f.data.length) || '?'} rows →`}
                </span>
              )}
            </div>
            <div className="finding-title">{f.title}</div>
            <div className="finding-detail">{f.detail}</div>
            {f.source && (
              <div className="finding-source">
                <span className="provenance-cite">{f.source}</span>
              </div>
            )}
            {/* Provenance sources — clickable */}
            {hasProv && (
              <div className="finding-provenance">
                <div className="finding-prov-label">📎 Sources ({f.provenance.length})</div>
                <div className="finding-prov-chips">
                  {f.provenance.slice(0, 4).map((p, pi) => {
                    const meta = p.metadata || {}
                    const label = meta.file_name
                      ? meta.file_name.replace(/\.[^.]+$/, '').replace(/_/g, ' ')
                      : p.type === 'sql_query' ? 'SQL Query'
                      : 'Source'
                    const truthLabel = truthTypeLabels[meta.truth_type] || ''
                    const relevance = p.distance != null ? Math.round((1 - p.distance) * 100) : null
                    return (
                      <button
                        key={pi}
                        className="prov-chip"
                        onClick={(e) => {
                          e.stopPropagation()
                          onProvenanceClick?.(p)
                        }}
                        title={`${meta.file_name || 'Source'} · ${meta.functional_area || ''} · Click to view`}
                      >
                        <span className="prov-chip-label">{label.length > 25 ? label.substring(0, 22) + '…' : label}</span>
                        {truthLabel && <span className="prov-chip-truth">{truthLabel}</span>}
                        {relevance != null && <span className="prov-chip-score">{relevance}%</span>}
                      </button>
                    )
                  })}
                  {f.provenance.length > 4 && (
                    <span className="prov-chip-more">+{f.provenance.length - 4} more</span>
                  )}
                </div>
              </div>
            )}
            {/* Pin to Profile button — only for findings with SQL */}
            {onPin && f.sql && (
              <button
                className="finding-pin-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  onPin(f)
                }}
                title="Pin this query as a metric on the customer profile"
              >
                📌 Pin to Profile
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
